import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { BOOKING_SOURCE, PAYMENT_OPTIONS } from "pagesV2/pro/Bookings/constants"
import {
  allocateIncludedCapacity,
  calculateTicketedBookingTotal,
  calculateWidgetBookingTotal,
  getFixedCardFeeShares,
  getMultiDayBooking,
  getProgressivePricing
} from "utils/widgetBookingPricing"
import { getCardFeeFraction, grossUpCardFee, resolveCardFeeConfig, roundToCents } from "utils/bookingCalculations"
import { numberToCurrency } from "helpers/string"
import { getPowerTranzCardFee } from "helpers/powertranz"
import { isTicketedSelection } from "../bookingDescription"
import { getBookableTicketTypes } from "../helpers"
import { groupBreakdownLines } from "../WidgetExperience/pricing"

dayjs.extend(customParseFormat)

// Checkout radio value -> the booking's payment_option (same mapping as the old widget).
export const CHECKOUT_PAYMENT_OPTIONS = {
  full: PAYMENT_OPTIONS.full_now,
  cash: PAYMENT_OPTIONS.deposit_cash,
  later: PAYMENT_OPTIONS.deposit_later
}

export const PAYMENT_METHODS = { card: "card", googlePay: "google_pay", applePay: "apple_pay", paypal: "paypal", powertranz: "powertranz" }

// start_time / end_time of the booking. Single-day slots carry both. A multi-day booking sends
// neither: its start time is fixed by the schedule for the start date, and the backend overwrites
// whatever is sent.
export const getBookingTimes = (selection, isMultiDay) => {
  const slot = selection?.timeSlot
  if (!slot || isMultiDay) return { start_time: undefined, end_time: undefined }
  const [start, end] = String(slot).split(" - ")
  return { start_time: start, end_time: end }
}

// Included vs extra guest counts, split as the old widget does: flat-rate capacity goes to adults,
// then children, then infants; passengers are always extra on a flat rate. Per-person bookings count
// every guest as included.
export const splitGuestCounts = (selection) => {
  const totals = selection?.counts?.main || {}
  const counts = {
    adults: Number(totals.adults || 0),
    children: Number(totals.children || 0),
    infants: Number(totals.infants || 0),
    passengers: Number(totals.passengers || 0)
  }
  const priceData = selection?.tripLength?.price || {}
  if (priceData.pricing_type !== "charge_flat_rate") {
    return { included: counts, extra: { adults: 0, children: 0, infants: 0, passengers: undefined } }
  }
  const includedGuests = priceData.included_guests ?? priceData.max_guests ?? 0
  const { extraAdults, extraChildren, extraInfants } = allocateIncludedCapacity(counts, includedGuests)
  return {
    included: { adults: counts.adults - extraAdults, children: counts.children - extraChildren, infants: counts.infants - extraInfants, passengers: 0 },
    extra: { adults: extraAdults, children: extraChildren, infants: extraInfants, passengers: counts.passengers }
  }
}

// Sent with every create and update from the widget, so the API checks the trip's Front-end
// Visibility (shown on the guide's site) instead of the Marketplace toggle. An update without it
// is rejected for a trip shown only on the guide's site.
export const EMBEDDED_BOOKING_PARAMS = { embedded: true }

// The guest's details, sent the same way for every booking.
const getGuestFields = (values) => ({
  client_attributes: {
    first_name: values?.first_name?.trim(),
    last_name: values?.last_name?.trim(),
    email: values?.email?.trim(),
    phone_number: values?.phone_number?.fullNumber,
    country_code: values?.phone_number?.countryCode
  },
  accessibility_enabled: values?.accessibility_enabled,
  accessibility_notes: values?.accessibility_enabled ? values?.accessibility_notes : undefined
})

const getAddOnLines = (selection) =>
  Object.entries(selection?.addOns || {})
    .filter(([, quantity]) => Number(quantity) > 0)
    .map(([addOnId, quantity]) => ({ add_on_id: Number(addOnId) || addOnId, quantity: Number(quantity) }))

const getTicketLines = (selection) =>
  Object.entries(selection.ticketCounts || {})
    .filter(([, quantity]) => Number(quantity) > 0)
    .map(([ticketTypeId, quantity]) => ({ ticket_type_id: Number(ticketTypeId) || ticketTypeId, quantity: Number(quantity) }))

// A ticketed booking (backend C3): the start time's id and the tickets per type; the server sets the
// date, times and guest count from them. Both can only be set on create, so an update (a retry)
// leaves them out.
const buildTicketedBooking = ({ selection, values, isUpdate }) => ({
  ...getGuestFields(values),
  ...(!isUpdate && { experience_time_slot_id: selection.scheduledSlot.id, ticket_lines: getTicketLines(selection) }),
  booking_source: BOOKING_SOURCE.embedded,
  booking_add_ons_attributes: getAddOnLines(selection)
})

const getGuestCountFields = (selection) => {
  const { included, extra } = splitGuestCounts(selection)
  return {
    adult_count: included.adults,
    child_count: included.children,
    infant_count: included.infants,
    passenger_count: included.passengers,
    extra_adult_count: extra.adults,
    extra_child_count: extra.children,
    extra_infant_count: extra.infants,
    extra_passenger_count: extra.passengers
  }
}

// POST /cart/holds and PATCH /cart/holds/:id: what a cart item holds, with the booking's own fields
// and guest split, since Pay must match its hold. `client_item_id` is the cart item's id. A multi-day
// trip runs from its schedule's start, so it sends no start time. Add-ons have no stock to hold.
export const buildCartHoldPayload = (item) => {
  const base = { experience_id: item.experienceId, client_item_id: item.cartId }
  if (isTicketedSelection(item)) return { ...base, experience_time_slot_id: item.scheduledSlot.id, ticket_lines: getTicketLines(item) }
  return {
    ...base,
    booked_date: item.date,
    start_time: getBookingTimes(item, item.isMultiDay).start_time,
    trip_price_id: item.tripLength?.key,
    ...getGuestCountFields(item)
  }
}

// POST/PUT /embedded_experiences payload. Private bookings: field for field what the old widget's
// checkout sends. A create from the cart names the item's hold (`holdId`), which the booking takes
// over; it must match the hold. A dynamic multi-day booking names its length (`durationDays`), as the
// old widget does; a fixed one takes it from trip_price_id.
export const buildBookingPayload = ({ experienceId, selection, values, isMultiDay, durationDays = 0, isUpdate = false, holdId = null }) => {
  const hold = !isUpdate && holdId ? { hold_id: holdId } : {}
  if (isTicketedSelection(selection)) {
    return { id: experienceId, ...EMBEDDED_BOOKING_PARAMS, ...hold, booking: buildTicketedBooking({ selection, values, isUpdate }) }
  }
  const { start_time, end_time } = getBookingTimes(selection, isMultiDay)
  return {
    id: experienceId,
    ...EMBEDDED_BOOKING_PARAMS,
    ...hold,
    booking: {
      ...getGuestFields(values),
      booked_date: selection?.date,
      ...(durationDays ? { duration_days: durationDays } : {}),
      ...getGuestCountFields(selection),
      start_time,
      end_time,
      trip_price_id: selection?.tripLength?.key,
      booking_source: BOOKING_SOURCE.embedded,
      booking_add_ons_attributes: getAddOnLines(selection)
    }
  }
}

// One cart item in POST /orders: its hold and the booking it becomes, which must match the hold
// (as a single booking from the cart). The server prices it, so no amounts are sent.
const buildOrderItem = (item) => {
  const base = { client_item_id: item.cartId, hold_id: item.holdId, experience_id: item.experienceId, booking_add_ons_attributes: getAddOnLines(item) }
  if (isTicketedSelection(item)) return { ...base, experience_time_slot_id: item.scheduledSlot.id, ticket_lines: getTicketLines(item) }
  return {
    ...base,
    booked_date: item.date,
    ...getBookingTimes(item, item.isMultiDay),
    trip_price_id: item.tripLength?.key,
    ...getGuestCountFields(item)
  }
}

// POST /orders: the cart's items booked together, with one contact, payment option and promo code.
export const buildOrderPayload = ({ items, values, paymentOption, promoCode }) => ({
  ...getGuestFields(values),
  payment_option: CHECKOUT_PAYMENT_OPTIONS[paymentOption] || CHECKOUT_PAYMENT_OPTIONS.full,
  ...(promoCode && { promo_code: promoCode }),
  booking_source: BOOKING_SOURCE.embedded,
  items: items.map(buildOrderItem)
})

// PUT /orders/:uuid before paying again: what the guest may have changed since the order was made.
// The email can't change (it identifies the guest); "" removes the promo code.
export const buildOrderUpdatePayload = ({ values, paymentOption, promoCode }) => {
  const { client_attributes, ...guestFields } = getGuestFields(values)
  const { email, ...client } = client_attributes
  return {
    ...guestFields,
    client_attributes: client,
    payment_option: CHECKOUT_PAYMENT_OPTIONS[paymentOption] || CHECKOUT_PAYMENT_OPTIONS.full,
    promo_code: promoCode || ""
  }
}

const firstValue = (value) => (Array.isArray(value) ? value[0] : value)

// Errors about the guest's own details. The API names the first cart item with them too, but they
// aren't about an experience.
const CONTACT_ERROR_KEYS = ["first_name", "last_name", "email", "phone_number", "country_code"]

// A refused order call: the error's key and message, and the cart item it is about (client_item_id)
// when it names one. { key, message, cartId }; key is null when the API gave none.
export const getOrderError = (error) => {
  const messages = error?.data?.message
  if (!messages || typeof messages !== "object") return { key: null, message: null, cartId: null }
  const key = Object.keys(messages).find((name) => name !== "client_item_id") || null
  const cartId = CONTACT_ERROR_KEYS.includes(key) ? null : firstValue(messages.client_item_id) ?? null
  return { key, message: key ? firstValue(messages[key]) : null, cartId }
}

// What a failed ticketed create says about availability (backend C6), by its message key: the start
// time is gone, closed or full ({ kind: "slot" }), or a ticket type has fewer places left
// ({ kind: "tickets", ticketTypeId, remaining }). Null for any other error, whose text is shown as is.
export const getAvailabilityError = (error) => {
  const message = error?.data?.message
  if (!message || typeof message !== "object") return null
  if (message.slot_unavailable) return { kind: "slot" }
  if (!message.tickets_unavailable) return null
  const ticketTypeId = firstValue(message.ticket_type_id)
  const remaining = Number(firstValue(message.remaining))
  if (ticketTypeId == null || !Number.isFinite(remaining)) return null
  return { kind: "tickets", ticketTypeId: Number(ticketTypeId) || ticketTypeId, remaining: Math.max(0, remaining) }
}

// When the booking starts: a ticketed start time's own instant, else date + start time, or the
// start of the day without one.
const getBookingStart = (selection) => {
  if (selection?.scheduledSlot?.startsAt) return dayjs(selection.scheduledSlot.startsAt)
  if (!selection?.date) return null
  const startLabel = String(selection?.timeSlot || "").split(" - ")[0].trim()
  const withTime = startLabel ? dayjs(`${selection.date} ${startLabel}`, ["YYYY-MM-DD h:mm A", "YYYY-MM-DD hh:mm A"], true) : null
  return withTime?.isValid() ? withTime : dayjs(selection.date).startOf("day")
}

// A booking that starts within 24 hours needs the guide's approval: in an order it becomes a request
// the guide accepts or declines (the API's requires_approval). Worked out from the start time, as the
// booking doesn't exist yet.
export const isWithinApprovalWindow = ({ selection, now = dayjs() }) => {
  const start = getBookingStart(selection)
  return !!start && start.diff(now, "hour", true) < 24
}

// Deposit options aren't offered when the trip requires full payment, has no deposit, or starts
// within 24 hours (the old widget hides them on the backend's less_then_24h flag).
export const canPayDeposit = ({ experience, selection, now = dayjs() }) => {
  const priceData = selection?.tripLength?.price || {}
  if (experience?.full_payment_required || priceData.full_payment_required) return false
  if (!(Number(experience?.deposit_percentage) > 0)) return false
  return !isWithinApprovalWindow({ selection, now })
}

// Everything checkout shows for one payment option: the booking total for that option (the card
// fee depends on what is charged by card now), what the card is charged today and the balance —
// the same formulas as the old widget's payment step — plus the total before day/seasonal
// adjustments and before promo codes, for the struck-through prices. Ticketed bookings are priced
// per ticket type.
// `cardFeeShare` is the booking's share of the card fee's fixed part in an order (getOrderBookingAmounts).
// `isPowerTranzCheckout`: the card is charged through PowerTranz, so the processing fee is the guide's
// own PowerTranz rate (no fixed part) instead of Stripe's.
export const getCheckoutAmounts = ({ experience, selection, promos, paymentOption, cardFeeShare = 1, isPowerTranzCheckout = false }) => {
  const depositPercent = Number(experience?.deposit_percentage) || 0
  const isDeposit = paymentOption !== "full"
  const depositFraction = getCardFeeFraction({ depositOnly: isDeposit, depositPercent })
  const isTicketed = isTicketedSelection(selection)
  const calculate = isTicketed ? calculateTicketedBookingTotal : calculateWidgetBookingTotal
  const booking = isTicketed
    ? { ticketTypes: getBookableTicketTypes(experience), ticketCounts: selection?.ticketCounts }
    : {
        priceData: selection?.tripLength?.price || {},
        counts: selection?.counts,
        multiDay: getMultiDayBooking({ experience, priceData: selection?.tripLength?.price, date: selection?.date, endDate: selection?.endDate })
      }
  const shared = {
    ...booking,
    date: selection?.date,
    addOnSelections: selection?.addOns || {},
    tripAddOns: experience?.add_ons || [],
    customFees: experience?.assigned_custom_fees || [],
    cardFeePassthroughEnabled: !isPowerTranzCheckout && experience?.platform_fee_setting?.card_fee_passthrough_enabled === true,
    // The processing fee is shown below the Order Total, as on the guide's screens (QA Ticketing #30),
    // so it stays out of the price lines and Taxes & Fees.
    isCardFeeListed: false,
    depositFraction,
    cardFeeShare,
    dayAdjustments: experience?.day_adjustments,
    seasonalAdjustments: experience?.seasonal_adjustments,
    availableDateRanges: experience?.available_date_ranges,
    progressivePricing: getProgressivePricing(experience)
  }
  const withPromos = calculate({ ...shared, discountPromos: promos })
  const withoutPromos = calculate({ ...shared, discountPromos: [] })
  const original = calculate({ ...shared, dayAdjustments: [], seasonalAdjustments: [], progressivePricing: null, discountPromos: [] })

  const cardFeeConfig = resolveCardFeeConfig({ customFees: experience?.assigned_custom_fees, platformFeeSetting: experience?.platform_fee_setting })
  const cardFee = isPowerTranzCheckout
    ? getPowerTranzCardFee(experience?.platform_fee_setting)
    : { ...cardFeeConfig, fixed: cardFeeConfig.fixed * cardFeeShare }
  const preCardTotal = Math.max(0, withPromos.preCardTotal)
  const depositBase = (preCardTotal * depositPercent) / 100
  // A deposit (balance in cash or later by card) carries the card fee on the deposit only; a balance
  // paid later by card earns its own fee when it is collected.
  const depositChargeNow = depositBase + grossUpCardFee(depositBase, cardFee)
  const chargeNowByOption = {
    full: preCardTotal + grossUpCardFee(preCardTotal, cardFee),
    cash: depositChargeNow,
    later: depositChargeNow
  }
  // What is charged today before the processing fee: the total, or the deposit.
  const chargeBaseByOption = { full: preCardTotal, cash: depositBase, later: depositBase }

  return {
    breakdown: withoutPromos.breakdown,
    // Totals before the processing fee (it is added below the Order Total).
    subtotal: withoutPromos.preCardTotal,
    originalSubtotal: original.preCardTotal,
    total: preCardTotal,
    guestSubtotal: withoutPromos.guestSubtotal,
    addOnsTotal: withoutPromos.addOnsTotal,
    // The price before taxes and fees (guests + add-ons) and what the promo code takes off it; taxes
    // and fees are then worked out on what is left (QA #24).
    baseSubtotal: withoutPromos.guestSubtotal + withoutPromos.addOnsTotal,
    promoDiscount: withPromos.discountAmount || 0,
    // Taxes & Fees as charged (after the promo code).
    taxesAndFees: groupBreakdownLines(withPromos.breakdown).taxesAndFees,
    chargeNowByOption,
    chargeBaseByOption,
    balanceByOption: { full: 0, cash: preCardTotal - depositBase, later: preCardTotal - depositBase },
    chargeNow: chargeNowByOption[paymentOption] ?? chargeNowByOption.full,
    chargeBase: chargeBaseByOption[paymentOption] ?? chargeBaseByOption.full,
    balance: isDeposit ? preCardTotal - depositBase : 0,
    preCardTotal
  }
}

// Each booking's amounts ([{ experience, selection, promos, isRequest }]) for one payment option. In
// an order of several the card fee's fixed part is charged once (client, Q33), shared by the bookings'
// totals; a request (isRequest) is charged on its own when the guide accepts, so it carries its own.
export const getOrderBookingAmounts = (bookings, paymentOption) => {
  const amountsList = bookings.map((booking) => getCheckoutAmounts({ ...booking, paymentOption }))
  if (amountsList.length < 2) return amountsList
  const sharedShares = getFixedCardFeeShares(amountsList.filter((_, index) => !bookings[index].isRequest).map((amounts) => amounts.preCardTotal))
  let sharedIndex = 0
  const shares = bookings.map((booking) => (booking.isRequest ? 1 : sharedShares[sharedIndex++]))
  return bookings.map((booking, index) => getCheckoutAmounts({ ...booking, paymentOption, cardFeeShare: shares[index] }))
}

// The order's amounts as the guest pays them. By card (and wallets) a request is charged only when
// the guide accepts, so today's charge leaves the requests out and `approvalCharge` is what they
// add later; by PayPal everything is charged now. `bookings`: [{ isRequest, amounts }].
export const getAmountsChargedToday = (amounts, bookings, isChargedOnApproval) => {
  const requests = isChargedOnApproval ? bookings.filter((booking) => booking.isRequest) : []
  if (!requests.length) return { ...amounts, approvalCharge: 0 }
  const requestsSum = (field, option) => requests.reduce((total, booking) => total + booking.amounts[field][option], 0)
  const withoutRequests = (field) =>
    Object.fromEntries(Object.entries(amounts[field]).map(([option, value]) => [option, value - requestsSum(field, option)]))
  const approvalCharge = requests.reduce((total, booking) => total + booking.amounts.chargeNow, 0)
  return {
    ...amounts,
    chargeNowByOption: withoutRequests("chargeNowByOption"),
    chargeBaseByOption: withoutRequests("chargeBaseByOption"),
    chargeNow: amounts.chargeNow - approvalCharge,
    chargeBase: amounts.chargeBase - requests.reduce((total, booking) => total + booking.amounts.chargeBase, 0),
    approvalCharge
  }
}

// What the API's order charges now: by card the bookings that don't wait for the guide's approval
// (each request is charged when accepted), by PayPal the whole order.
export const getOrderChargeNow = (order, isChargedOnApproval) => {
  if (!isChargedOnApproval) return Number(order?.amounts?.charge_now)
  return (order?.bookings || []).filter((booking) => !booking.requires_approval).reduce((total, booking) => total + Number(booking.amounts?.charge_now || 0), 0)
}

const ORDER_AMOUNT_FIELDS = [
  "subtotal",
  "originalSubtotal",
  "total",
  "guestSubtotal",
  "addOnsTotal",
  "baseSubtotal",
  "promoDiscount",
  "taxesAndFees",
  "chargeNow",
  "chargeBase",
  "balance"
]
const OPTION_AMOUNT_FIELDS = ["chargeNowByOption", "chargeBaseByOption", "balanceByOption"]

// The order's amounts with several experiences: each booking's amounts (getCheckoutAmounts) added
// up, so the order total, today's charge and the balance are the bookings' together (with one card
// fee when the amounts come from getOrderBookingAmounts).
export const combineCheckoutAmounts = (amountsList) => {
  const sum = (pick) => amountsList.reduce((total, amounts) => total + (pick(amounts) || 0), 0)
  const combined = Object.fromEntries(ORDER_AMOUNT_FIELDS.map((field) => [field, sum((amounts) => amounts[field])]))
  OPTION_AMOUNT_FIELDS.forEach((field) => {
    combined[field] = Object.fromEntries(Object.keys(amountsList[0]?.[field] || {}).map((option) => [option, sum((amounts) => amounts[field][option])]))
  })
  return { ...combined, breakdown: [] }
}

// Each booking's promo codes in an order ([{ key, experience, selection }]) from the order's code
// (`cartIds`: the bookings it covers). A percentage code applies as is to every booking it covers. A
// fixed amount comes off the order once: it is split across the covered bookings in proportion to what
// each can be discounted (the same shares as the card fee's fixed part), in cents with the remainder
// on the last one, as the API splits it.
export const getOrderBookingPromos = (bookings, promo) => {
  const isCovered = (booking) => !!promo?.cartIds?.includes(booking.key)
  if (!promo || promo.discount_type === "percentage") return bookings.map((booking) => (isCovered(booking) ? [promo] : []))
  const covered = bookings.filter(isCovered)
  const bases = covered.map((booking) => {
    const amounts = getCheckoutAmounts({ ...booking, promos: [], paymentOption: "full" })
    return amounts.guestSubtotal + (promo.apply_to_add_ons ? amounts.addOnsTotal : 0)
  })
  const shares = getFixedCardFeeShares(bases)
  const valueByKey = {}
  let rest = promo.value
  covered.forEach((booking, index) => {
    const value = index === covered.length - 1 ? roundToCents(rest) : roundToCents(promo.value * shares[index])
    valueByKey[booking.key] = value
    rest -= value
  })
  return bookings.map((booking) => (booking.key in valueByKey ? [{ ...promo, value: valueByKey[booking.key] }] : []))
}

// The promo code as the pricing applies it, from the API's validation result.
export const toDiscountPromo = (result, code) => ({
  type: "discount",
  code: result.code ?? code,
  name: result.name,
  discount_type: result.discount_type,
  value: Number(result.value),
  apply_to_add_ons: result.applicability_rules?.apply_to_add_ons ?? true,
  label: result.label ?? (result.discount_type === "percentage" ? `-${Number(result.value)}%` : `-${numberToCurrency(result.value)}`)
})
